import React, { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Switch,
  NumberInput,
  Select,
  Button,
  Divider,
  Alert,
  Box,
  Paper,
  Slider,
} from '@mantine/core';
import { IconBulb, IconSettings, IconInfoCircle } from '@tabler/icons-react';
import type { ModelInfo } from '@gofranz/gpt-muse-common';
import { ReasoningSupport, ReasoningFormat, ModelCapability } from '@gofranz/llm-core-common';

export interface ReasoningSettings {
  reasoningEnabled: boolean;
  maxReasoningTokens: number;
  reasoningEffort?: string; // For OpenAI models
  excludeReasoning: boolean;
}

export interface WebSearchSettings {
  webSearchEnabled: boolean;
  webSearchContextSize?: number;
}

export interface ChatSettings {
  chatMemoryLimit: number; // 0 = unlimited, 1-500 = number of messages
}

interface SettingsModalProps {
  opened: boolean;
  onClose: () => void;
  selectedModel: ModelInfo | undefined;
  onSettingsChange: (modelId: string, settings: ReasoningSettings) => void;
  initialSettings?: ReasoningSettings;
  onChatSettingsChange: (settings: ChatSettings) => void;
  initialChatSettings?: ChatSettings;
  onWebSearchSettingsChange: (modelId: string, settings: WebSearchSettings) => void;
  initialWebSearchSettings?: WebSearchSettings;
}

const DEFAULT_SETTINGS: ReasoningSettings = {
  reasoningEnabled: true,
  maxReasoningTokens: 10000,
  reasoningEffort: 'medium',
  excludeReasoning: false,
};

const DEFAULT_CHAT_SETTINGS: ChatSettings = {
  chatMemoryLimit: 20, // Default to 20 messages
};

const DEFAULT_WEB_SEARCH_SETTINGS: WebSearchSettings = {
  webSearchEnabled: false, // Default to disabled
  webSearchContextSize: 5000, // Default context size for web search
};

const EFFORT_OPTIONS = [
  { value: 'low', label: 'Low - Faster, less thorough' },
  { value: 'medium', label: 'Medium - Balanced speed and quality' },
  { value: 'high', label: 'High - Slower, more thorough' },
];

export const SettingsModal = React.memo<SettingsModalProps>(({
  opened,
  onClose,
  selectedModel,
  onSettingsChange,
  initialSettings,
  onChatSettingsChange,
  initialChatSettings,
  onWebSearchSettingsChange,
  initialWebSearchSettings,
}) => {
  const [settings, setSettings] = useState<ReasoningSettings>(DEFAULT_SETTINGS);
  const [chatSettings, setChatSettings] = useState<ChatSettings>(DEFAULT_CHAT_SETTINGS);
  const [webSearchSettings, setWebSearchSettings] = useState<WebSearchSettings>(DEFAULT_WEB_SEARCH_SETTINGS);

  // Load settings when modal opens or model changes
  useEffect(() => {
    let loadedSettings = initialSettings || DEFAULT_SETTINGS;
    
    // For mandatory reasoning models, always ensure reasoning is enabled
    if (selectedModel?.reasoning_support === ReasoningSupport.Mandatory) {
      loadedSettings = { ...loadedSettings, reasoningEnabled: true };
    }
    
    setSettings(loadedSettings);
  }, [initialSettings, selectedModel]);

  // Load chat settings when modal opens
  useEffect(() => {
    if (initialChatSettings) {
      setChatSettings(initialChatSettings);
    } else {
      setChatSettings(DEFAULT_CHAT_SETTINGS);
    }
  }, [initialChatSettings]);

  // Load web search settings when modal opens
  useEffect(() => {
    if (initialWebSearchSettings) {
      setWebSearchSettings(initialWebSearchSettings);
    } else {
      setWebSearchSettings(DEFAULT_WEB_SEARCH_SETTINGS);
    }
  }, [initialWebSearchSettings]);

  const handleSave = () => {
    if (selectedModel) {
      const finalSettings = { ...settings };
      
      // For mandatory reasoning models, always ensure reasoning is enabled
      if (selectedModel.reasoning_support === ReasoningSupport.Mandatory) {
        finalSettings.reasoningEnabled = true;
      }
      
      onSettingsChange(selectedModel.id, finalSettings);
      onWebSearchSettingsChange(selectedModel.id, webSearchSettings);
    }
    onChatSettingsChange(chatSettings);
    onClose();
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    setChatSettings(DEFAULT_CHAT_SETTINGS);
    setWebSearchSettings(DEFAULT_WEB_SEARCH_SETTINGS);
  };

  const usesEffortFormat = selectedModel?.reasoning_format === ReasoningFormat.Effort;
  const hasReasoningSupport = selectedModel?.reasoning_support !== ReasoningSupport.None;
  const isMandatoryReasoning = selectedModel?.reasoning_support === ReasoningSupport.Mandatory;
  const supportsWebSearch = selectedModel?.capabilities.includes(ModelCapability.WebSearch);

  if (!selectedModel) {
    return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconSettings size={20} />
          <Text size="lg" fw={600}>Chat Settings</Text>
        </Group>
      }
      size="md"
      centered
    >
      <Stack gap="md">
        {/* Chat Settings - Global */}
        <Paper p="sm" withBorder>
          <Text size="sm" fw={600} mb="sm">Chat Settings (Global)</Text>
          
          <Box>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>Chat Memory</Text>
              <Text size="xs" c="dimmed">
                {chatSettings.chatMemoryLimit === 0 ? 'Unlimited' : `${chatSettings.chatMemoryLimit} messages`}
              </Text>
            </Group>
            
            <Slider
              value={chatSettings.chatMemoryLimit}
              onChange={(value) => 
                setChatSettings(prev => ({ ...prev, chatMemoryLimit: value }))
              }
              min={0}
              max={500}
              step={1}
              marks={[
                { value: 0, label: '0' },
                { value: 20, label: '20' },
                { value: 100, label: '100' },
                { value: 250, label: '250' },
                { value: 500, label: '500' },
              ]}
              mb="lg"
            />
            
            <Text size="xs" c="dimmed" mt="md">
              {chatSettings.chatMemoryLimit === 0 
                ? 'Send all previous messages (may hit context limits with long conversations)'
                : `Only send the last ${chatSettings.chatMemoryLimit} messages to the AI. Reduces costs and prevents context limits.`
              }
              <br />
              Default: 20 messages (good balance of context and efficiency)
            </Text>
          </Box>
        </Paper>

        <Divider label="Model-Specific Settings" />

        {/* Model Info */}
        <Paper p="sm" withBorder>
          <Group justify="space-between" align="flex-start">
            <Box>
              <Text size="sm" fw={600}>{selectedModel.name}</Text>
              <Text size="xs" c="dimmed">{selectedModel.id}</Text>
            </Box>
            <Group gap={4}>
              {selectedModel.reasoning_support === ReasoningSupport.Mandatory && (
                <Text size="xs" c="yellow">
                  <IconBulb size={12} style={{ marginRight: 2 }} />
                  Reasoning Always On
                </Text>
              )}
              {selectedModel.reasoning_support === ReasoningSupport.Optional && (
                <Text size="xs" c="orange">
                  <IconBulb size={12} style={{ marginRight: 2 }} />
                  Reasoning Optional
                </Text>
              )}
              {selectedModel.reasoning_support === ReasoningSupport.None && (
                <Text size="xs" c="gray">
                  No Reasoning
                </Text>
              )}
            </Group>
          </Group>
        </Paper>

        {!hasReasoningSupport && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title="No Reasoning Support"
            color="gray"
          >
            This model does not support reasoning capabilities.
          </Alert>
        )}

        {hasReasoningSupport && (
          <>
            {/* Enable/Disable Reasoning */}
            <Group justify="space-between">
              <Box>
                <Text size="sm" fw={500}>Enable Reasoning</Text>
                <Text size="xs" c="dimmed">
                  {isMandatoryReasoning 
                    ? "Reasoning is always enabled for this model"
                    : "Turn reasoning on or off for this model"
                  }
                </Text>
              </Box>
              <Switch
                checked={settings.reasoningEnabled || isMandatoryReasoning}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setSettings(prev => ({ ...prev, reasoningEnabled: checked }));
                }}
                disabled={isMandatoryReasoning}
              />
            </Group>


            {(settings.reasoningEnabled || isMandatoryReasoning) && (
              <>
                <Divider />

                {/* Reasoning Effort (OpenAI/Grok) or Max Tokens (Anthropic/Gemini/DeepSeek) */}
                {usesEffortFormat ? (
                  <Box>
                    <Text size="sm" fw={500} mb="xs">Reasoning Effort</Text>
                    <Select
                      data={EFFORT_OPTIONS}
                      value={settings.reasoningEffort}
                      onChange={(value) => 
                        setSettings(prev => ({ ...prev, reasoningEffort: value || 'medium' }))
                      }
                      description="Controls how much effort the model puts into reasoning"
                    />
                  </Box>
                ) : (
                  <Box>
                    <Text size="sm" fw={500} mb="xs">Max Reasoning Tokens</Text>
                    <NumberInput
                      value={settings.maxReasoningTokens}
                      onChange={(value) => 
                        setSettings(prev => ({ ...prev, maxReasoningTokens: typeof value === 'number' ? value : 10000 }))
                      }
                      min={1000}
                      max={50000}
                      step={1000}
                      description="Maximum tokens the model can use for reasoning"
                    />
                  </Box>
                )}

                <Divider />

                {/* Exclude Reasoning from Response */}
                <Group justify="space-between">
                  <Box>
                    <Text size="sm" fw={500}>Hide Reasoning</Text>
                    <Text size="xs" c="dimmed">
                      Use reasoning internally but don't show it in the response
                    </Text>
                  </Box>
                  <Switch
                    checked={settings.excludeReasoning}
                    onChange={(event) => {
                      const checked = event.currentTarget.checked;
                      setSettings(prev => ({ ...prev, excludeReasoning: checked }));
                    }}
                  />
                </Group>
              </>
            )}
          </>
        )}

        {/* Web Search Settings */}
        {supportsWebSearch && (
          <>
            <Divider label="Web Search Settings" />

            <Group justify="space-between">
              <Box>
                <Text size="sm" fw={500}>Enable Web Search</Text>
                <Text size="xs" c="dimmed">
                  Allow the model to search the web for current information
                </Text>
              </Box>
              <Switch
                checked={webSearchSettings.webSearchEnabled}
                onChange={(event) => {
                  const checked = event.currentTarget.checked;
                  setWebSearchSettings(prev => ({ ...prev, webSearchEnabled: checked }));
                }}
              />
            </Group>

            {webSearchSettings.webSearchEnabled && (
              <>
                <Box>
                  <Text size="sm" fw={500} mb="xs">Search Context Size</Text>
                  <NumberInput
                    value={webSearchSettings.webSearchContextSize}
                    onChange={(value) =>
                      setWebSearchSettings(prev => ({ ...prev, webSearchContextSize: typeof value === 'number' ? value : 5000 }))
                    }
                    min={1000}
                    max={20000}
                    step={1000}
                    description="Maximum tokens to use for web search context (recommended: 5000)"
                  />
                </Box>
              </>
            )}
          </>
        )}

        <Group justify="space-between" mt="md">
          <Button
            variant="subtle"
            onClick={handleReset}
            disabled={!hasReasoningSupport && !supportsWebSearch}
          >
            Reset to Defaults
          </Button>
          <Group gap="sm">
            <Button variant="light" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Settings
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
});

SettingsModal.displayName = 'SettingsModal';